import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
/* actions */
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllPABXs } from '../../redux/actions/pabxs';
import { getAllDevices, editDevices } from '../../redux/actions/devices';
import ErrorMsgField from '../../components/ErrorMsgField';
/* components */
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/device';
/* utils */
import handleChangeDefault from '../../utils/handleChangeDefault';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    name: '',
    initials: '',
    type: '',
    department: '',
    pabx: '',
    number: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const departments = useSelector(state => state.departments);
  const pabxs = useSelector(state => state.pabxs);
  const devices = useSelector(state => state.devices);
  const device = devices.data.find(dpt => dpt._id === id);
  const [data, setData] = useState(INITIAL_VALUES);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllPABXs());
    dispatch(getAllDevices());
  }, [dispatch]);

  useEffect(() => {
    if (device) {
      const newData = {
        ...device,
        department: device.department._id || '',
        pabx: device.pabx._id || ''
      };
      setData(newData);
    }
  }, [device]);

  const submit = () => {
    const newData = { ...data };
    if (data.type !== 'G') {
      delete newData.initials;
    }
    if (data.type !== 'G' && data.type !== 'E' && data.type !== 'L') {
      delete newData.name;
    }
    dispatch(editDevices(newData, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {devices.editSuccess && (
        <AlertMsg type="success" msg="Dispositivo atualizado com sucesso" />
      )}
      {devices.errorContent &&
        !devices.errorContent.message.includes('400') &&
        devices.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização do disposivo"
        />
      )}
      {devices.errorContent &&
        devices.errorContent.message.includes('400') &&
        devices.editError && (
        <AlertMsg type="danger" msg="O número informado já está cadastrado" />
      )}
      {!devices.editSuccess && !devices.editError && (
        <AlertMsg type="info" msg="Área atualização de disposivos" />
      )}
      <FormHeader title="Atualizar Dispositivo" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <label htmlFor="type">
            Tipo<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="type"
            name="type"
            value={data.type}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            <option value="E">Extensão</option>
            <option value="G">Grupo</option>
            <option value="N">Tronco</option>
            <option value="U">OGM</option>
            <option value="L">Linha</option>
          </select>
          {errors.type && errors.type.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        {(data.type === 'E' || data.type === 'G' || data.type === 'L') && (
          <div className="form-group">
            <label htmlFor="name">
              Nome<span className="field-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              autoComplete="off"
              onChange={evt => handleChangeDefault(evt, data, setData)}
              defaultValue={data.name || ''}
              ref={register}
            />
            {errors.name && errors.name.type === 'required' && (
              <ErrorMsgField type="required" />
            )}
          </div>
        )}
        {data.type === 'G' && (
          <div className="form-group">
            <label htmlFor="initials">
              Sigla<span className="field-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="initials"
              name="initials"
              autoComplete="off"
              onChange={evt => handleChangeDefault(evt, data, setData)}
              defaultValue={data.initials || ''}
              ref={register}
            />
            {errors.initials && errors.initials.type === 'required' && (
              <ErrorMsgField type="required" />
            )}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="department">
            Departamento<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="department"
            name="department"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.department}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {departments.data.map((dpt, i) => {
              const _id = dpt._id || Date.now() + '' + i;
              return (
                <option key={_id} value={_id}>
                  {dpt.name}
                </option>
              );
            })}
          </select>
          {errors.department && errors.department.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="pabx">
            PABX<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="pabx"
            name="pabx"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.pabx}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {pabxs.data.map((pabx, i) => {
              const _id = pabx._id || Date.now() + '' + i;
              return (
                <option key={_id} value={_id}>
                  {pabx.name} | {pabx.ip}
                </option>
              );
            })}
          </select>
          {errors.pabx && errors.pabx.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="number">
            Número<span className="field-required">*</span>
          </label>
          <input
            type="text"
            autoComplete="off"
            className="form-control input-number"
            id="number"
            name="number"
            number="on"
            defaultValue={data.number}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          />
          {errors.number && errors.number.type === 'integer' && (
            <ErrorMsgField type="integer" />
          )}
        </div>
        <FormFooter url="/devices" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
